import { countBy, uniqBy } from 'lodash'

import { SplitsAvatar } from '~/components/SplitsAvatar'
import { UserAvatar } from '~/components/UserAvatar'
import { OrgMember, OrgSigner, OrgSmartAccount } from '~/types'
import { shortenAddress } from '~/utils/addresses'
import { cn } from '~/utils/tailwind-utils'

export const AccountSignersCard = ({
  account,
}: {
  account: OrgSmartAccount
}) => {
  const userSigners = getUniqueUserSigners(account.signers)
  const signerCountByUserId = countBy(
    account.signers.filter((signer) => signer.user),
    (signer) => signer.user!.id,
  )

  return (
    <div className="flex flex-col gap-2">
      {userSigners.map((signer) => {
        const count = signerCountByUserId[signer.user!.id] ?? 0
        return (
          <div
            key={signer.user!.id}
            className="flex min-w-40 items-center gap-2"
          >
            <UserAvatar user={signer.user as OrgMember} size={20} />
            <span className="min-w-0 flex-1 truncate">
              {getSignerUserDisplayName(signer.user as OrgMember)}
            </span>
            {count > 1 && (
              <span className="tabular-nums text-secondary">x{count}</span>
            )}
          </div>
        )
      })}
      {account.eoaSigners.map((signer) => (
        <div key={signer.id} className="flex min-w-40 items-center gap-2">
          <SplitsAvatar address={signer.address} size={20} />
          <span className="min-w-0 flex-1 truncate">
            {signer.name || shortenAddress(signer.address)}
          </span>
        </div>
      ))}
      <AccountThreshold account={account} />
    </div>
  )
}

const AccountThreshold = ({ account }: { account: OrgSmartAccount }) => {
  const totalSigners = account.signers.length + account.eoaSigners.length
  const threshold = account.threshold
  const radius = 9
  const circumference = 2 * Math.PI * radius
  const progress = Math.min(Math.max(threshold / totalSigners, 0), 1)
  const filledStrokeLength = circumference * progress

  if (totalSigners <= 1) return null

  return (
    <>
      <div className="h-px w-full bg-gray-200 dark:bg-gray-700" />
      <div className="flex items-center gap-2">
        <svg aria-hidden className="size-5 -rotate-90" viewBox="0 0 24 24">
          <circle
            className="stroke-gray-200 dark:stroke-gray-700"
            cx="12"
            cy="12"
            r={radius}
            fill="none"
            strokeWidth="3"
          />
          <circle
            className="stroke-primary"
            cx="12"
            cy="12"
            r={radius}
            fill="none"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${filledStrokeLength} ${circumference}`}
          />
        </svg>
        <span className="tabular-nums text-primary">
          {threshold} of {totalSigners}
        </span>
      </div>
    </>
  )
}

const MAX_SIGNER_AVATARS = 5

// ringClassName must match the surface behind the pile (incl. hover) so the
// overlap rings blend
export const SignerFacePile = ({
  account,
  includeEoaSigners = false,
  size = 24,
  ringClassName = 'ring-white dark:ring-black',
}: {
  account: OrgSmartAccount
  includeEoaSigners?: boolean
  size?: number
  ringClassName?: string
}) => {
  const userSigners = getUniqueUserSigners(account.signers)
  const eoaSigners = includeEoaSigners ? account.eoaSigners : []
  const totalCount = userSigners.length + eoaSigners.length
  const visibleUserSigners = userSigners.slice(0, MAX_SIGNER_AVATARS)
  const visibleAvatars = [
    ...visibleUserSigners.map((signer) => ({
      key: signer.user!.id,
      node: <UserAvatar user={signer.user as OrgMember} size={size} />,
    })),
    ...eoaSigners
      .slice(0, MAX_SIGNER_AVATARS - visibleUserSigners.length)
      .map((signer) => ({
        key: signer.id,
        node: <SplitsAvatar address={signer.address} size={size} />,
      })),
  ]
  const hiddenCount = totalCount - visibleAvatars.length

  if (totalCount === 0) return null

  return (
    <div className="flex items-center">
      <div className="flex -space-x-1.5">
        {visibleAvatars.map((avatar, index) => (
          <div
            key={avatar.key}
            className={cn('rounded-full ring-2', ringClassName)}
            style={{ zIndex: visibleAvatars.length - index }}
          >
            {avatar.node}
          </div>
        ))}
      </div>
      {hiddenCount > 0 && (
        <span className="ml-1 text-sm font-medium text-primary">
          +{hiddenCount}
        </span>
      )}
    </div>
  )
}

const getSignerUserDisplayName = (user: OrgMember) => {
  return user.displayName ?? user.email
}

export const getUniqueUserSigners = (signers: OrgSigner[]) =>
  uniqBy(
    signers.filter((signer) => signer.user),
    (signer) => signer.user!.id,
  )

// Orphan passkey signers have no avatar, so an account can have signers the
// pile can't render — callers gate the pile (and its hovercard) on this.
export const hasSignerAvatars = (account: OrgSmartAccount) =>
  getUniqueUserSigners(account.signers).length + account.eoaSigners.length > 0
